import { APITable } from "server/services/tables/types/tables";
import type { Project, Page } from "@prisma/client";
import { isProduction, isStaging } from "utils/environment";
import { config } from "server/config";
import { TabSlug } from "components/ProjectSettings/ProjectSettings";
import { SideNavDrawerType } from "blocks/types";

export const BASE_PATH = "/interfaces";

export function getInterfacesUrl() {
  return config().NEXT_PUBLIC_INTERFACES_BASE_URL;
}

export function getPageUrl(
  projectSlug: Project["slug"],
  pageSlug: Page["slug"],
  customDomain?: Project["customDomain"]
) {
  let url = publishedPagePath({
    projectSlug,
    pageSlug,
  });
  if (customDomain) {
    url = customDomainPagePath({
      customDomain,
      pageSlug,
    });
  }
  return url;
}

export function customDomainPagePath(props: {
  customDomain: Project["customDomain"];
  pageSlug: Page["slug"];
}): string {
  const protocol = isProduction || isStaging ? "https://" : "http://";
  return `${protocol}${props.customDomain}/${props.pageSlug}`;
}
export function customDomainProjectPath(props: {
  customDomain: Project["customDomain"];
}): string {
  const protocol = isProduction || isStaging ? "https://" : "http://";
  return `${protocol}${props.customDomain}`;
}

export function publishedPagePath(props: {
  projectSlug: Project["slug"];
  pageSlug: Page["slug"];
}): string {
  const protocol = isProduction || isStaging ? "https://" : "http://";
  const domain = config().NEXT_PUBLIC_BASE_DOMAIN;
  return `${protocol}${props.projectSlug}.${domain}/${props.pageSlug}`;
}

export function publishedProjectPath(props: {
  projectSlug: Project["slug"];
}): string {
  const protocol = isProduction || isStaging ? "https://" : "http://";
  const domain = config().NEXT_PUBLIC_BASE_DOMAIN;
  return `${protocol}${props.projectSlug}.${domain}`;
}

export function publishedProjectUrl(args: {
  projectSlug: Project["slug"];
  customDomain: Project["customDomain"] | undefined;
  customDomainConfigured: boolean | undefined;
}): string {
  if (args.customDomain && args.customDomainConfigured)
    return customDomainProjectPath({ customDomain: args.customDomain });
  return publishedProjectPath({ projectSlug: args.projectSlug });
}

export function projectsPath(props: {
  accountId?: Project["accountId"];
}): string {
  const queryString = props.accountId ? `?accountId=${props.accountId}` : "";
  return `${BASE_PATH}${queryString}`;
}

export function projectPath(props: { projectId: Project["id"] }): string {
  return `${BASE_PATH}/project/${props.projectId}`;
}

export function projectSettingsPath(props: {
  projectId: Project["id"];
  tab?: TabSlug;
}): string {
  const queryString = props.tab ? `?tab=${props.tab}` : "";
  return `${projectPath(props)}/settings${queryString}`;
}

export function projectConsumerManagementPath(props: {
  projectId: Project["id"];
}): string {
  return `${projectSettingsPath(props)}/users`;
}

export function pagePath(props: {
  pageId: Page["id"];
  sideNavDrawer?: SideNavDrawerType;
}) {
  const queryString = props.sideNavDrawer
    ? `?sideNavDrawer=${props.sideNavDrawer}`
    : "";
  return `${BASE_PATH}/page/${props.pageId}${queryString}`;
}

export function webComponentAssetsPath(): string {
  return `${
    config().NEXT_PUBLIC_INTERFACES_BASE_URL
  }/assets/web-components/zapier-interfaces/zapier-interfaces.esm.js`;
}

// External
// -----------------------------------------------------------------------------

export function getEditZapUrl(props: {
  rootNodeId: string;
  accountId: number | undefined;
}) {
  return `${config().NEXT_PUBLIC_ZAPIER_API_BASE_URL}/editor/${
    props.rootNodeId
  }?context=${props.accountId}`;
}

export function getZapbotsIndexUrl(): string {
  return config().NEXT_PUBLIC_ZAPIER_ZAPBOTS_UI_BASE_URL;
}

export function getTablesIndexUrl(): string {
  return `${config().NEXT_PUBLIC_ZAPIER_TABLES_UI_BASE_URL}/app/tables`;
}

export function getEditTableUrl(props: { tableId: APITable["id"] }): string {
  return `${config().NEXT_PUBLIC_ZAPIER_TABLES_UI_BASE_URL}/app/tables/t/${
    props.tableId
  }`;
}

export const officeHoursUrl =
  "https://zapier-interfaces.zapier.app/weekly-office-hours";

export function getHelpUrl(): string {
  return "https://zapier-interfaces.zapier.app/home";
}

export function getCallUrl(): string {
  return "https://zapier-interfaces.zapier.app/schedule";
}

/**
 * Modeled after the `getZapierBaseUrl` function in the Tables UI codebase,
 * modified to use `config().NEXT_PUBLIC_ZAPIER_API_BASE_URL`.
 * @see https://gitlab.com/zapier/team-fpv/tablesui/-/blob/staging/packages/tablesui/src/api/getZapierBaseUrl.ts#L4
 * @returns The base URL for the Zapier API, or an empty string if we're in development mode
 */
export const getZapierBaseUrl = () => {
  const baseUrl = config().NEXT_PUBLIC_ZAPIER_API_BASE_URL;
  if (
    baseUrl === "https://zapier.com" ||
    baseUrl === "https://zapier-staging.com"
  ) {
    return baseUrl;
  }

  // let our local development proxy kick in to fake out the CORS cookies
  return "";
};

export function getStripePaymentLink(): string {
  return config().NEXT_PUBLIC_STRIPE_PAYMENT_LINK;
}
